import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    inject,
    OnDestroy,
    OnInit,
    signal,
    TemplateRef,
    viewChild
} from '@angular/core';
import { IconDefinition } from '@fortawesome/angular-fontawesome';
import { faArrowLeft as falArrowLeft, faTimer as falTimer } from '@fortawesome/pro-light-svg-icons';
import {
    faHourglassEnd as farHourglassEnd,
    faHourglassHalf as farHourglassHalf,
    faHourglassStart as farHourglassStart
} from '@fortawesome/pro-regular-svg-icons';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
    ButtonComponent,
    IconComponent,
    isNullOrUndefined,
    ModalUtils,
    tryToUnsubscribeFromSubscription
} from '@swissfex/sfx-commons';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzNotificationDataOptions } from 'ng-zorro-antd/notification';
import { filter, interval, Subscription } from 'rxjs';

import { environment } from '../../../environments/environment';
import { TimePipe } from '../../common-utils/pipes/time.pipe';
import { LOGIN_QUERY, LOGIN_QUERY_LOGOUT_REASON_MAP, LOGOUT_REASON } from '../../constants';
import { PopupNotificationTemplate } from '../../constants/types';
import { PopupNotificationService } from '../../core/services/popup-notification.service';
import { RouterService } from '../../core/services/router.service';
import { UserInactivityService } from '../../core/services/user-inactivity.service';
import { wait } from '../../core/utils/utils';
import { ALREADY_CONFIRMED_LEAVING_PROJECT_NAVIGATION_EXTRAS } from '../../projects/edit-project/constants';
import { AuthenticationService, IAuthStatus } from '../../projects/services/authentication.service';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-session-status-notification',
    templateUrl: './session-status-notification.component.html',
    styleUrl: './session-status-notification.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgTemplateOutlet, AsyncPipe, TranslatePipe, TimePipe, ButtonComponent, IconComponent]
})
export class SessionStatusNotificationComponent implements OnInit, AfterViewInit, OnDestroy {
    private readonly authenticationSvc = inject(AuthenticationService);
    private readonly inactivitySvc = inject(UserInactivityService);
    private readonly modalService = inject(NzModalService);
    private readonly popupNotificationSvc = inject(PopupNotificationService);
    private readonly routerSvc = inject(RouterService);
    private readonly translate = inject(TranslateService);

    public prolongNotificationTpl = viewChild<PopupNotificationTemplate>('prolongNotificationTpl');
    public inactivityNotificationTpl = viewChild<PopupNotificationTemplate>('inactivityNotificationTpl');
    public shortProlongNotificationTpl = viewChild<PopupNotificationTemplate>('shortProlongNotificationTpl');
    public closeIconTpl = viewChild<TemplateRef<void>>('closeIconTpl');

    public hourglassIcon = signal<IconDefinition>(farHourglassStart);
    public logoutTimestamp = signal<number>(undefined);
    public userInactivityLogoutTimestamp = signal<number>(undefined);

    private prolongNotificationElement: Element;
    private inactivityNotificationElement: Element;
    private shortProlongNotificationElement: Element;
    private hourglassIconChangeIntervalId: ReturnType<typeof setInterval>;
    private authStatus: IAuthStatus;
    private prolongSessionDisplayTimestamp: number;
    private isUserInactiveSinceTimestamp: number;
    private isSessionProlongationDisplayed: boolean;
    private _timerInterval: Subscription;
    private _displayProlongNotificationTimeout: ReturnType<typeof setTimeout>;
    private _displayProlongShortNotificationTimeout: ReturnType<typeof setTimeout>;
    private _removeProlongNotificationAnimationClassTimeout: ReturnType<typeof setTimeout>;
    private _removeProlongShortNotificationAnimationClassTimeout: ReturnType<typeof setTimeout>;
    private _removeInactivityNotificationAnimationClassTimeout: ReturnType<typeof setTimeout>;

    private readonly prolongSessionTimeBeforeExpiration = 10 * 60 * 1000;
    private readonly notificationKey = 'session-status-notification';
    private readonly inactivityNotificationKey = 'session-status-notification--inactivity';
    private readonly commonNotificationClassName = 'session-status__notification-wrapper';
    private readonly standardNotificationClassName = 'session-status__standard-notification-wrapper';
    private readonly inactivityNotificationClassName = `${this.standardNotificationClassName} session-status__standard-notification-wrapper--inactivity`;
    private readonly shortNotificationClassName = 'session-status__short-notification-wrapper';
    private readonly slideUpNotificationClassName = 'session-status__notification-wrapper--slide-up';
    private readonly slideLeftNotificationClassName = 'session-status__notification-wrapper--slide-left';
    private readonly slideRightNotificationClassName = 'session-status__notification-wrapper--slide-right';
    private readonly commonAnimationDuration = 400;
    private readonly leaveAnimationDuration = 200;
    private readonly commonNotificationOptions: NzNotificationDataOptions = {
        nzDuration: 0,
        nzPlacement: 'bottomLeft',
        nzAnimate: false,
        nzClass: this.commonNotificationClassName,
        nzKey: this.notificationKey
    };
    private readonly hourglassIconNextStates: Record<string, IconDefinition> = {
        [farHourglassStart.iconName]: farHourglassHalf,
        [farHourglassHalf.iconName]: farHourglassEnd,
        [farHourglassEnd.iconName]: farHourglassStart
    };

    protected readonly falTimer = falTimer;
    protected readonly falArrowLeft = falArrowLeft;

    public ngOnInit(): void {
        this.authenticationSvc.authStatus.pipe(untilDestroyed(this)).subscribe(async (authStatus: IAuthStatus) => {
            await this.processAuthStatusChange(authStatus);
        });

        this.inactivitySvc.userInactivityObservable
            .pipe(
                untilDestroyed(this),
                filter(() => this.authStatus?.isAuthorized)
            )
            .subscribe(async (isInactive: boolean) => {
                const now = Date.now();
                if (isInactive) {
                    this.isUserInactiveSinceTimestamp = now;
                    this.userInactivityLogoutTimestamp.set(
                        Math.min(now + (environment.inactivityTimeout?.logout || 0), this.logoutTimestamp() || 0)
                    );
                } else {
                    this.isUserInactiveSinceTimestamp = null;
                    this.userInactivityLogoutTimestamp.set(null);
                }

                await this.updateNotificationsState(now);
            });
    }

    public ngAfterViewInit(): void {
        this.commonNotificationOptions.nzCloseIcon = this.closeIconTpl();
    }

    public async ngOnDestroy(): Promise<void> {
        this.inactivitySvc.stopInactivityTracking();
        await this.clearUpComponent();
    }

    // eslint-disable-next-line @typescript-eslint/related-getter-setter-pairs
    private get timerInterval(): Subscription {
        return this._timerInterval;
    }

    private set timerInterval(delegate: () => Subscription) {
        tryToUnsubscribeFromSubscription(this._timerInterval);
        this._timerInterval = delegate();
    }

    // eslint-disable-next-line @typescript-eslint/related-getter-setter-pairs
    private get displayProlongNotificationTimeout(): ReturnType<typeof setTimeout> {
        return this._displayProlongNotificationTimeout;
    }

    private set displayProlongNotificationTimeout(delegate: () => ReturnType<typeof setTimeout>) {
        clearTimeout(this._displayProlongNotificationTimeout);
        this._displayProlongNotificationTimeout = delegate();
    }

    // eslint-disable-next-line @typescript-eslint/related-getter-setter-pairs
    private get displayProlongShortNotificationTimeout(): ReturnType<typeof setTimeout> {
        return this._displayProlongShortNotificationTimeout;
    }

    private set displayProlongShortNotificationTimeout(delegate: () => ReturnType<typeof setTimeout>) {
        clearTimeout(this._displayProlongShortNotificationTimeout);
        this._displayProlongShortNotificationTimeout = delegate();
    }

    // eslint-disable-next-line @typescript-eslint/related-getter-setter-pairs
    private get removeProlongNotificationAnimationClassTimeout(): ReturnType<typeof setTimeout> {
        return this._removeProlongNotificationAnimationClassTimeout;
    }

    private set removeProlongNotificationAnimationClassTimeout(delegate: () => ReturnType<typeof setTimeout>) {
        clearTimeout(this._removeProlongNotificationAnimationClassTimeout);
        this._removeProlongNotificationAnimationClassTimeout = delegate();
    }

    // eslint-disable-next-line @typescript-eslint/related-getter-setter-pairs
    private get removeProlongShortNotificationAnimationClassTimeout(): ReturnType<typeof setTimeout> {
        return this._removeProlongShortNotificationAnimationClassTimeout;
    }

    private set removeProlongShortNotificationAnimationClassTimeout(delegate: () => ReturnType<typeof setTimeout>) {
        clearTimeout(this._removeProlongShortNotificationAnimationClassTimeout);
        this._removeProlongShortNotificationAnimationClassTimeout = delegate();
    }

    // eslint-disable-next-line @typescript-eslint/related-getter-setter-pairs
    private get removeInactivityNotificationAnimationClassTimeout(): ReturnType<typeof setTimeout> {
        return this._removeInactivityNotificationAnimationClassTimeout;
    }

    private set removeInactivityNotificationAnimationClassTimeout(delegate: () => ReturnType<typeof setTimeout>) {
        clearTimeout(this._removeInactivityNotificationAnimationClassTimeout);
        this._removeInactivityNotificationAnimationClassTimeout = delegate();
    }

    private getNotificationElement(className: string): Element {
        const selectorClasses = className.replaceAll(' ', '.');

        return document.querySelector(`.${selectorClasses}`);
    }

    private async checkIfAnyTimestampExpired(): Promise<void> {
        const now = Date.now();

        if (
            (!!this.userInactivityLogoutTimestamp() && this.userInactivityLogoutTimestamp() <= now) ||
            (!!this.logoutTimestamp() && this.logoutTimestamp() <= now)
        ) {
            void this.onTimerCompleted();
            return;
        }

        await this.updateNotificationsState(now);
    }

    private setLogoutTimestamp(): void {
        this.logoutTimestamp.set(this.authStatus?.tokenExpiration || 0);
    }

    public async onProlongSessionClicked(): Promise<void> {
        await this.clearUpComponent();
        await this.authenticationSvc.prolongSession();
    }

    public async removeStandardProlongNotification(shouldDisplayShortProlongNotification = true): Promise<void> {
        await this.removeNotification(
            'prolongNotificationElement',
            this.notificationKey,
            shouldDisplayShortProlongNotification
                ? () =>
                      (this.displayProlongShortNotificationTimeout = () =>
                          setTimeout(() => {
                              if (this.authStatus?.isAuthorized) {
                                  this.displayShortNotification();
                              }
                          }, 300))
                : undefined
        );
    }

    public async removeShortProlongNotification(): Promise<void> {
        this.clearHourglassIconChangeInterval();
        await this.removeNotification(
            'shortProlongNotificationElement',
            this.notificationKey,
            () =>
                (this.displayProlongNotificationTimeout = () =>
                    setTimeout(() => {
                        if (this.authStatus?.isAuthorized) {
                            this.displayStandardNotification();
                        }
                    }, 300))
        );
    }

    private async processAuthStatusChange(authStatus: IAuthStatus): Promise<void> {
        const oldAuthStatus = this.authStatus;
        this.authStatus = authStatus;
        this.setLogoutTimestamp();
        const didJustGetUnauthenticated =
            oldAuthStatus && oldAuthStatus.isAuthenticated && !this.authStatus?.isAuthenticated;

        if (this.authStatus?.isAuthenticated) {
            if (this.authStatus.isAuthorized) {
                this.processLoggedInImplications();
            } else {
                this.inactivitySvc.stopInactivityTracking();
            }
        } else {
            await this.processLoggedOutImplications(didJustGetUnauthenticated);
        }
    }

    private processLoggedInImplications(): void {
        this.inactivitySvc.startInactivityTracking();
        this.setUpTokenLifecycleEventsForManualProlongation();

        this.timerInterval = () =>
            interval(1000)
                .pipe(untilDestroyed(this))
                .subscribe(async () => {
                    await this.checkIfAnyTimestampExpired();
                });
    }

    private async processLoggedOutImplications(didJustGetUnauthenticated: boolean): Promise<void> {
        this.inactivitySvc.stopInactivityTracking();
        await this.clearUpComponent();
        if (didJustGetUnauthenticated) {
            void this.onTimerCompleted();
        }
    }

    private setUpTokenLifecycleEventsForManualProlongation(): void {
        if (!this.authenticationSvc.doesIdentityProviderSupportRefreshToken(this.authStatus.activeProvider)) {
            // NO REFRESHING TOKEN SCENARIO
            if (!!this.authStatus.tokenExpiration) {
                this.prolongSessionDisplayTimestamp = Math.max(
                    this.authStatus.tokenExpiration - this.prolongSessionTimeBeforeExpiration,
                    Date.now()
                );
            }
        }
    }

    private async onProlongSessionTimeoutExpired(): Promise<void> {
        this.prolongSessionDisplayTimestamp = null;
        this.isSessionProlongationDisplayed = true;

        const { isConfirmedPromise } = ModalUtils.waitForConfirmation(
            this.modalService,
            this.translate.instant('auth.prolong_session.title'),
            this.translate.instant('auth.prolong_session.message'),
            this.translate.instant('auth.prolong_session.prolong'),
            this.translate.instant('auth.prolong_session.cancel'),
            undefined,
            undefined,
            undefined,
            undefined,
            1001
        );
        if (await isConfirmedPromise) {
            await this.onProlongSessionClicked();
        } else {
            this.displayStandardNotification();
        }
    }

    private async onTimerCompleted(): Promise<void> {
        await this.clearUpComponent();
        await this.routerSvc.navigateToLogout({
            queryParams: {
                [LOGIN_QUERY_LOGOUT_REASON_MAP[LOGOUT_REASON.SESSION_EXPIRED]]: LOGOUT_REASON.SESSION_EXPIRED,
                [LOGIN_QUERY.RETURN_URL]: this.routerSvc.getValidReturnUrl(this.routerSvc.currentUrl)
            },
            ...ALREADY_CONFIRMED_LEAVING_PROJECT_NAVIGATION_EXTRAS
        });
    }

    private displayInactivityNotification(): void {
        if (this.inactivityNotificationElement) {
            return;
        }

        this.displayNotification('inactivityNotificationElement', this.inactivityNotificationTpl(), {
            ...this.commonNotificationOptions,
            nzClass: `${this.commonNotificationOptions.nzClass as string} ${this.inactivityNotificationClassName} ${this.slideUpNotificationClassName}`,
            nzKey: this.inactivityNotificationKey,
            nzAnimate: false
        });

        this.removeInactivityNotificationAnimationClassTimeout = () =>
            setTimeout(() => {
                if (this.inactivityNotificationElement) {
                    this.inactivityNotificationElement.classList.remove(this.slideUpNotificationClassName);
                }
            }, this.commonAnimationDuration);
    }

    public async removeInactivityNotification(): Promise<void> {
        await this.removeNotification('inactivityNotificationElement', this.inactivityNotificationKey);
    }

    private displayStandardNotification(): void {
        if (this.prolongNotificationElement) {
            return;
        }

        this.displayNotification('prolongNotificationElement', this.prolongNotificationTpl(), {
            ...this.commonNotificationOptions,
            nzClass: `${this.commonNotificationOptions.nzClass as string} ${this.standardNotificationClassName} ${this.slideUpNotificationClassName}`,
            nzAnimate: false
        });

        this.removeProlongNotificationAnimationClassTimeout = () =>
            setTimeout(() => {
                if (this.prolongNotificationElement) {
                    this.prolongNotificationElement.classList.remove(this.slideUpNotificationClassName);
                }
            }, this.commonAnimationDuration);
    }

    private displayShortNotification(): void {
        if (this.shortProlongNotificationElement) {
            return;
        }

        this.displayNotification('shortProlongNotificationElement', this.shortProlongNotificationTpl(), {
            ...this.commonNotificationOptions,
            nzClass: `${this.commonNotificationOptions.nzClass as string} ${this.shortNotificationClassName} ${this.slideRightNotificationClassName}`,
            nzAnimate: false
        });

        this.removeProlongShortNotificationAnimationClassTimeout = () =>
            setTimeout(() => {
                if (this.shortProlongNotificationElement) {
                    this.shortProlongNotificationElement.classList.remove(this.slideRightNotificationClassName);
                }
            }, this.commonAnimationDuration);

        this.startHourglassIconChangeInterval();
    }

    private displayNotification(
        contextPropName: string,
        notificationTemplate: PopupNotificationTemplate,
        notificationOptions: NzNotificationDataOptions
    ): void {
        if (this[contextPropName] || !this.authStatus?.isAuthorized) {
            return;
        }

        if (notificationTemplate) {
            this.popupNotificationSvc.createNotificationWithTemplate(notificationTemplate, notificationOptions);
            this[contextPropName] = this.getNotificationElement(notificationOptions.nzClass as string);
        }
    }

    private async removeNotification(
        notificationContextPropName: string,
        notificationKey: string,
        onNotificationRemovedFn?: () => void
    ): Promise<void> {
        if (this[notificationContextPropName]) {
            this[notificationContextPropName].classList.add(this.slideLeftNotificationClassName);
            await wait(this.leaveAnimationDuration);
            this.processNotificationRemove(notificationKey, onNotificationRemovedFn);
            this[notificationContextPropName] = null;
        } else {
            this.processNotificationRemove(notificationKey, onNotificationRemovedFn);
        }
    }

    private processNotificationRemove(notificationKey: string, onNotificationRemovedFn?: () => void): void {
        this.popupNotificationSvc.removeNotification(notificationKey);

        if (onNotificationRemovedFn) {
            onNotificationRemovedFn();
        }
    }

    private startHourglassIconChangeInterval(): void {
        this.clearHourglassIconChangeInterval();

        this.hourglassIconChangeIntervalId = setInterval(() => {
            this.hourglassIcon.set(this.hourglassIconNextStates[this.hourglassIcon()?.iconName] || farHourglassStart);
        }, 800);
    }

    private clearHourglassIconChangeInterval(): void {
        if (this.hourglassIconChangeIntervalId) {
            clearInterval(this.hourglassIconChangeIntervalId);
        }
    }

    private async removeAnyNotification(): Promise<void> {
        await this.removeNotification('prolongNotificationElement', this.notificationKey);
        await this.removeNotification('shortProlongNotificationElement', this.notificationKey);
        this.clearHourglassIconChangeInterval();
        await this.removeNotification('inactivityNotificationElement', this.inactivityNotificationKey);
    }

    private async updateNotificationsState(timestamp?: number): Promise<void> {
        const now = timestamp || Date.now();

        if (!isNullOrUndefined(this.userInactivityLogoutTimestamp())) {
            if (this.isUserInactiveSinceTimestamp <= now) {
                if (this.isSessionProlongationDisplayed && this.prolongNotificationElement) {
                    await this.removeStandardProlongNotification();
                }
                this.displayInactivityNotification();
            }
        } else {
            await this.removeInactivityNotification();
        }

        if (!isNullOrUndefined(this.prolongSessionDisplayTimestamp)) {
            if (this.prolongSessionDisplayTimestamp <= now) {
                if (!this.isSessionProlongationDisplayed) {
                    void this.onProlongSessionTimeoutExpired();
                }
            }
        }
    }

    private async clearUpComponent(): Promise<void> {
        this.isSessionProlongationDisplayed = false;
        this.isUserInactiveSinceTimestamp = null;
        this.prolongSessionDisplayTimestamp = null;
        this.userInactivityLogoutTimestamp.set(null);
        tryToUnsubscribeFromSubscription(this.timerInterval);
        clearTimeout(this.displayProlongNotificationTimeout);
        clearTimeout(this.displayProlongShortNotificationTimeout);
        clearTimeout(this.removeProlongNotificationAnimationClassTimeout);
        clearTimeout(this.removeProlongShortNotificationAnimationClassTimeout);
        clearTimeout(this.removeInactivityNotificationAnimationClassTimeout);
        this.setLogoutTimestamp();
        await this.removeAnyNotification();
    }
}
